import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Col } from "reactstrap";
import AsyncSelect from "react-select/async";
import CustomSelect from "components/customs/dropdown";
import { ACCOUNT_CONGRESSMAN, canEditRoles } from "utility/constants";
import {
  addProjectDropdownStyles,
  addProjectSearchInputStyle,
} from "constants/style";
import FormGenerator from "components/customs/form-generator/input";
import CustomCreatableSelect from "components/customs/creatable-select";
import CustomDropzone from "views/projects/add/Dropzone";
import useAuth from "hooks/useAuth";
import LoadingScreen from "components/customs/loading-screen";
import { ADD_ACTION, VIEW_ACTION } from "constants/actions";
import {
  AddButton,
  CancelButton,
  FormLabel,
  RequiredIndicator,
} from "assets/js/Styles";
import styled from "styled-components";
import useAgencies from "hooks/useAgencies";
import { AGENCY_TESDA_ID } from "constants/agency";
import { useNavigate } from "react-router";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import classNames from "classnames";
import AddCourseTable from "./Table";
import { acceptedFileTypes, selectorOptions } from "./constants";

const RowSection = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1.2rem;
`;

const StyledRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const FormColRight = styled.div`
  flex: 0 0 auto;
  width: 100%;

  @media (min-width: 768px) {
    flex: 0 0 auto;
    width: 50%;
  }

  .dropzone-container {
    width: auto;
    height: 65px;
    border: 1px dashed #a3a3a3;

    .upload-files {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
    }
  }

  .react-datepicker-wrapper {
    width: 100%;
  }
`;

const BtnSection = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 3rem;
  padding-bottom: 4rem;
  gap: 16px;
`;

const StyledCancelButton = styled(CancelButton)`
  width: 130px;
`;

const StyledAddButton = styled(AddButton)`
  background: ${({ theme }) => theme.colors.buttonFormBlue};
  width: 130px;
`;

const validationSchema = Yup.object().shape({
  projectId: Yup.object().required("Required"),
  provinceId: Yup.object().required("Required"),
  regionId: Yup.object().required("Required"),
  tviId: Yup.object().required("Required"),
  proponentType: Yup.object().required("Required"),
  id: Yup.object().when("proponentType", {
    is: (proponentType) => proponentType?.label === ACCOUNT_CONGRESSMAN,
    then: (schema) => schema.required("Required"),
    otherwise: (schema) => schema.nullable(),
  }),
  officialId: Yup.object().when("proponentType", {
    is: (proponentType) => proponentType?.label !== ACCOUNT_CONGRESSMAN,
    then: (schema) => schema.required("Required"),
    otherwise: (schema) => schema.nullable(),
  }),
});

const TVIEndorsementForm = ({
  handleProponentSearch,
  form,
  setForm,
  setSelectedPosition,
  handleUploadedFiles,
  handleDeleteFile,
  uploadedFiles,
  regionOptions,
  setSelectedCourses,
  selectedCourses,
  coursesOptions,
  action,
  projectOptions,
  isLoading,
  handleSaveForm,
  municipalityOptions,
  provinceOptions,
  setSelectedTVI,
  handleTviSearch,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { nameOptions: agencies } = useAgencies();

  const defaultAgency = useMemo(
    () => agencies?.find((option) => option.id === AGENCY_TESDA_ID),
    [agencies]
  );

  if (!user) {
    return <LoadingScreen />;
  }

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={form}
      enableReinitialize
      onSubmit={handleSaveForm}
    >
      {({ values, setFieldValue, handleChange }) => (
        <Form>
          <RowSection>
            <Col
              xs="12"
              md="6"
            >
              <FormLabel>{"Agency"}</FormLabel>
              <p>{"Provide the name of the agency"}</p>
            </Col>
            <FormColRight>
              <CustomSelect
                placeholder="Agency"
                styles={addProjectDropdownStyles}
                value={defaultAgency}
                options={agencies}
                filterOption={(option, searchValue) =>
                  option?.label
                    ?.toLowerCase()
                    ?.includes(searchValue?.toLowerCase()) ||
                  option?.data?.acronym
                    ?.toLowerCase()
                    ?.includes(searchValue?.toLowerCase())
                }
                isDisabled
              />
            </FormColRight>
          </RowSection>

          <Field name="projectId">
            {({ form: { touched, errors } }) => (
              <RowSection>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormLabel>
                    {"Program"}
                    {RequiredIndicator}
                  </FormLabel>
                  <p>{"Provide the name of the program"}</p>
                </Col>
                <FormColRight>
                  <CustomSelect
                    placeholder="Program"
                    styles={addProjectDropdownStyles}
                    options={projectOptions}
                    name="projectId"
                    value={values.projectId}
                    className={classNames({
                      "is-invalid": errors?.projectId && touched?.projectId,
                    })}
                    onChange={(option) => {
                      setFieldValue("projectId", option);
                    }}
                    isDisabled={action !== ADD_ACTION}
                  />
                  <ErrorMessage name="projectId">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </FormColRight>
              </RowSection>
            )}
          </Field>

          <hr />

          <Field name="proponentType">
            {({ form: { touched, errors } }) => (
              <RowSection>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormLabel>
                    {"Proponent"}
                    {RequiredIndicator}
                  </FormLabel>
                  <p>{"Provide the proponent"}</p>
                </Col>
                <FormColRight>
                  <CustomSelect
                    placeholder="Proponent Position"
                    onChange={(option) => {
                      setSelectedPosition(option);
                      setFieldValue("proponentType", option);
                      setFieldValue("congressmanId", null);
                      setFieldValue("officialId", null);
                    }}
                    name="proponentType"
                    value={values.proponentType}
                    className={classNames({
                      "is-invalid":
                        errors?.proponentType && touched?.proponentType,
                    })}
                    options={selectorOptions}
                    styles={addProjectDropdownStyles}
                    isDisabled={action !== ADD_ACTION}
                  />
                  <ErrorMessage name="proponentType">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </FormColRight>
              </RowSection>
            )}
          </Field>

          <Field name="id">
            {({ form: { touched, errors } }) => (
              <StyledRow>
                <Col
                  xs="12"
                  md="6"
                />
                <Col
                  xs="12"
                  md="6"
                >
                  <AsyncSelect
                    placeholder="Proponent Name"
                    onChange={(option) => {
                      if (values.proponentType?.label === ACCOUNT_CONGRESSMAN) {
                        setFieldValue("id", option);
                      } else {
                        setFieldValue("officialId", option);
                      }
                    }}
                    name="id"
                    loadOptions={handleProponentSearch}
                    styles={addProjectSearchInputStyle}
                    value={values.id || values.officialId}
                    className={classNames({
                      "is-invalid": errors?.id && touched?.id,
                    })}
                    isDisabled={!values.proponentType || action !== ADD_ACTION}
                  />
                  <ErrorMessage name="id">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                  <ErrorMessage name="officialId">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </StyledRow>
            )}
          </Field>

          <Field name="implementationDate">
            {({ form: { touched, errors } }) => (
              <RowSection>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormLabel>{"Implementation Date"}</FormLabel>
                  <p>{"Provide the implementation date of endorsement"}</p>
                </Col>
                <FormColRight>
                  <FormGenerator
                    type="datetime"
                    placeholderText="Date"
                    dateFormat="MMM-dd-yyyy"
                    name="implementationDate"
                    selected={values.implementationDate}
                    className={classNames({
                      "is-invalid":
                        errors?.implementationDate &&
                        touched?.implementationDate,
                    })}
                    onChange={(date) => {
                      setFieldValue("implementationDate", date);
                    }}
                    disabled={action !== ADD_ACTION}
                  />
                  <ErrorMessage name="implementationDate">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </FormColRight>
              </RowSection>
            )}
          </Field>

          <Field name="regionId">
            {({ form: { touched, errors } }) => (
              <RowSection>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormLabel>
                    {"Region"}
                    {RequiredIndicator}
                  </FormLabel>
                  <p>{"Provide the region"}</p>
                </Col>

                <FormColRight>
                  <CustomSelect
                    placeholder="Please Select a Region"
                    styles={addProjectDropdownStyles}
                    options={regionOptions}
                    name="regionId"
                    value={values.regionId}
                    className={classNames({
                      "is-invalid": errors?.regionId && touched?.regionId,
                    })}
                    onChange={(option) => {
                      setFieldValue("regionId", option);

                      setForm({
                        ...values,
                        regionId: option,
                      });
                    }}
                    isDisabled={action !== ADD_ACTION}
                  />
                  <ErrorMessage name="regionId">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </FormColRight>
              </RowSection>
            )}
          </Field>

          <Field name="provinceId">
            {({ form: { touched, errors } }) => (
              <RowSection>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormLabel>
                    {"Province"}
                    {RequiredIndicator}
                  </FormLabel>
                  <p>{"Provide the province in your region"}</p>
                </Col>
                <FormColRight>
                  <CustomSelect
                    placeholder="Please Select a Province"
                    styles={addProjectDropdownStyles}
                    options={provinceOptions}
                    name="provinceId"
                    value={values.provinceId}
                    className={classNames({
                      "is-invalid": errors?.provinceId && touched?.provinceId,
                    })}
                    onChange={(option) => {
                      setFieldValue("provinceId", option);

                      setForm({
                        ...values,
                        provinceId: option,
                      });
                    }}
                    isDisabled={action !== ADD_ACTION}
                  />
                  <ErrorMessage name="provinceId">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </FormColRight>
              </RowSection>
            )}
          </Field>

          <Field name="municipalityId">
            {({ form: { touched, errors } }) => (
              <RowSection>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormLabel>{"Municipality"}</FormLabel>
                  <p>{"Provide the municipality in your province"}</p>
                </Col>
                <FormColRight>
                  <CustomSelect
                    placeholder="Please Select a Municipality"
                    styles={addProjectDropdownStyles}
                    options={municipalityOptions}
                    name="municipalityId"
                    value={values.municipalityId}
                    className={classNames({
                      "is-invalid":
                        errors?.municipalityId && touched?.municipalityId,
                    })}
                    onChange={(option) => {
                      setFieldValue("municipalityId", option);
                    }}
                    isDisabled={action !== ADD_ACTION}
                  />
                  <ErrorMessage name="municipalityId">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </FormColRight>
              </RowSection>
            )}
          </Field>

          <Field name="tviId">
            {({ form: { touched, errors } }) => (
              <RowSection>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormLabel>
                    {"Name of TVI"}
                    {RequiredIndicator}
                  </FormLabel>
                  <p>{"Provide the TVI"}</p>
                </Col>
                <Col
                  xs="12"
                  md="6"
                >
                  <AsyncSelect
                    placeholder="TVI"
                    name="tviId"
                    value={values.tviId}
                    className={classNames({
                      "is-invalid": errors?.tviId && touched?.tviId,
                    })}
                    onChange={(option) => {
                      setSelectedTVI(option);
                      setFieldValue("tviId", option);
                    }}
                    loadOptions={handleTviSearch}
                    styles={addProjectSearchInputStyle}
                    isDisabled={!values.provinceId || action !== ADD_ACTION}
                  />
                  <ErrorMessage name="tviId">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </RowSection>
            )}
          </Field>

          {canEditRoles.includes(user?.type) && (
            <Field name="editorNotes">
              {({ form: { touched, errors } }) => (
                <RowSection>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <FormLabel>{"Editor Notes"}</FormLabel>
                    <p>{"Provide the editor notes"}</p>
                  </Col>
                  <FormColRight>
                    <CustomCreatableSelect
                      isMulti
                      placeholder="Editor Notes"
                      styles={addProjectDropdownStyles}
                      onChange={(option) => {
                        setFieldValue("editorNotes", option);
                      }}
                      name="editorNotes"
                      value={values.editorNotes}
                      className={classNames({
                        "is-invalid":
                          errors?.editorNotes && touched?.editorNotes,
                      })}
                      isDisabled={action !== ADD_ACTION}
                    />
                    <ErrorMessage name="editorNotes">
                      {(msg) => (
                        <small className="field-error text-danger">{msg}</small>
                      )}
                    </ErrorMessage>
                  </FormColRight>
                </RowSection>
              )}
            </Field>
          )}

          <RowSection>
            <Col
              xs="12"
              md="6"
            >
              <FormLabel>
                {"Supporting Documents"}
                {RequiredIndicator}
              </FormLabel>
              <p>{"Upload the supporting documents"}</p>
            </Col>
            <FormColRight>
              <CustomDropzone
                handleUploadedFiles={handleUploadedFiles}
                handleDeleteFile={handleDeleteFile}
                uploadedFiles={uploadedFiles}
                label="Upload Attachments"
                acceptedFileTypes={acceptedFileTypes}
                acceptedFileTypeErrorMsg="Accepted files are only xls, xlxs, and pdf."
                isDisabled={action !== ADD_ACTION}
              />
            </FormColRight>
          </RowSection>

          <Field name="justification">
            {({ form: { touched, errors } }) => (
              <RowSection>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormLabel>{"Justification"}</FormLabel>
                  <p>{"Provide the justification"}</p>
                </Col>
                <FormColRight>
                  <FormGenerator
                    type="textarea"
                    name="justification"
                    placeholder="Enter Text Here"
                    rows="5"
                    value={values.justification}
                    className={classNames({
                      "is-invalid":
                        errors?.justification && touched?.justification,
                    })}
                    onChange={(event) => {
                      handleChange(event);
                    }}
                    isDisabled={action !== ADD_ACTION}
                  />
                  <ErrorMessage name="justification">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </FormColRight>
              </RowSection>
            )}
          </Field>

          <AddCourseTable
            data={coursesOptions}
            setCourses={setSelectedCourses}
            setForm={setForm}
            courses={selectedCourses}
            action={action}
          />

          <BtnSection>
            <StyledCancelButton
              outline
              onClick={() => navigate("/settings/tesda")}
            >
              {"Cancel"}
            </StyledCancelButton>

            <StyledAddButton
              color="primary"
              type="submit"
              disabled={isLoading || action === VIEW_ACTION}
            >
              {"Save"}
            </StyledAddButton>
          </BtnSection>
        </Form>
      )}
    </Formik>
  );
};

TVIEndorsementForm.prototype = {
  handleSearchInputOnChange: PropTypes.func,
  index: PropTypes.number.isRequired,
  handleProponentSearch: PropTypes.func,
  handleDeleteRow: PropTypes.func,
  regionOptions: PropTypes.oneOfType([PropTypes.object]),
  recipient: PropTypes.oneOfType([PropTypes.object]),
  selectedProject: PropTypes.number,
};

export default TVIEndorsementForm;
